/** @format */

import {useEffect, useState} from 'react';

import {fetchAllSizes} from '../hoc/all_sizes_local_storage_handler';
import {
    QUOTER_ROUNDED_CORNER,
    QUOTER_SQUARE_CORNER,
    SHAPE_CIRCLE,
    SHAPE_CUSTOM,
    SHAPE_OVAL,
    SHAPE_RECTANGLE,
    SHAPE_SQUARE,
    SHAPE_CARTON,
    QUOTER_SHAPE_CIRCLE_OVAL,
    QUOTER_SHAPE_CUSTOM,
    QUOTER_SHAPE_CHAPSTICK,
    QUOTER_SHAPE_POLYGON,
    QUOTER_SHAPE_TAMPER_SEAL,
    QUOTER_SHAPE_NEW_SHAPE,
    QUOTER_SHAPE_SQUARE_RECTANGLE,
    QUOTER_SHAPE_PREVIOUSLY_USED,
    QUOTER_CUSTOM_SIZES,
    SIZE_TYPE_CUSTOM_NEW,
    SIZE_TYPE_STANDARD_NEW,
    PRODUCT_TYPE_CARTON,
    PRODUCT_TYPE_GRAPHIC_DESIGN,
    SHAPE_CARTON_AUTOLOCK,
    SHAPE_CARTON_STRAIGHT_TUCK,
    SHAPE_CARTON_CUSTOM
} from 'frontier-constants';
import useRequest from './use_request';

const initialState = {
    // The quoter's selected shape category, e.g. Square/Rectangle, Custom, etc.
    shapeCategory: null,
    // The sub category of shapeCategory if this is a custom shape.
    customShapeSubCategory: null,
    // True if the die is a "custom shape" (not rectangular or circular), but may still be
    // an existing die in our die inventory.
    isCustomShape: false,
    // True if this is a new die that does not exist in our die inventory.
    isNewDie: false,

    cartonDies: [],
    cartonCustomDies: [],

    corners: null,
    width: null,
    length: null,
    depth: null,
    diesMatchingShape: [],
    diesMatchingShapeAndSize: [],
    diesMatchingShapeOppositeCorners: [],
    selectedDie: null
};

/**
 * This hook manages state for values concerning die selection, and contains logic for
 * mapping the quoter's UI representation of shapes to what the backend understands, and
 * vice-versa. When values are set with the returned setters, this hook returns additional
 * derived state that includes dies matching the current shape, corners, width, and
 * length.
 *
 * @param {Object} options
 * @param {Object} options.order the current backend order object
 * @param {Function} options.updateOrder a callback for updating the order's state, which
 * will be called with a slice of updated order attributes
 */
export default function useDieSelection({order, updateOrder}) {
    const [state, setState] = useState(initialState);

    const {data} = useRequest([], fetchAllSizes, {initial: {sizes: []}});
    const dies = data.sizes;

    // Fires when we receive an update on certain order attributes from the backend.
    useEffect(() => {
        const nextState = mapOrderToState(state, order, dies);
        setState(nextState);
    }, [
        order.width,
        order.length,
        order.depth,
        order.size_id,
        order.size_type_id,
        order.shape_id,
        dies
    ]);

    // Callback used when any of the returned setters are called by e.g. their inputs.
    function localUpdate(patch) {
        const patchedState = {...state, ...patch};
        const productId =
            order.product_type_id === PRODUCT_TYPE_GRAPHIC_DESIGN
                ? order.graphic_design_order_details.design_product_type_id
                : order.product_type_id;
        const nextState = {...patchedState, ...findDies(patchedState, dies, productId)};
        setState(nextState);
        updateOrder(mapStateToOrder(nextState));
    }
    return {
        ...state,
        allDies: dies,
        setAllSizes: v =>
            localUpdate({
                width: parseFloat(v.width),
                length: parseFloat(v.length),
                depth: parseFloat(v.depth),
                is_design_in_progress: true
            }),
        setWidthLength: (width, length) => localUpdate({width, length}),
        setWidth: v => v && localUpdate({width: parseFloat(v)}),
        setLength: v => v && localUpdate({length: parseFloat(v)}),
        setDepth: v => v && localUpdate({depth: parseFloat(v)}),
        setShapeCategory: v =>
            localUpdate({
                shapeCategory: v,
                customShapeSubCategory: null
            }),
        setCustomShapeSubCategory: v => localUpdate({customShapeSubCategory: v}),
        setCorners: v => localUpdate({corners: v})
    };
}

function findDies(state, dies, productId) {
    const {width, length, depth, corners, shapeCategory, customShapeSubCategory} = state;

    // Find matching dies client-side.
    let diesMatchingShape;
    let diesMatchingShapeOppositeCorners;
    const oppositeCorners =
        corners === QUOTER_SQUARE_CORNER ? QUOTER_ROUNDED_CORNER : QUOTER_SQUARE_CORNER;
    if (shapeCategory === QUOTER_SHAPE_CUSTOM) {
        diesMatchingShape = dies.filter(dieShapeFilter(customShapeSubCategory, corners));
        diesMatchingShapeOppositeCorners = dies
            .filter(dieSizeFilter(width, length))
            .filter(dieShapeFilter(customShapeSubCategory, oppositeCorners));
    } else {
        diesMatchingShape = dies.filter(dieShapeFilter(shapeCategory, corners));
        diesMatchingShapeOppositeCorners = dies
            .filter(dieSizeFilter(width, length))
            .filter(dieShapeFilter(shapeCategory, oppositeCorners));
    }

    let cartonDies;
    cartonDies = dies.filter(die => die.product_type_id === PRODUCT_TYPE_CARTON);
    const diesMatchingShapeAndSize =
        width && length ? diesMatchingShape.filter(dieSizeFilter(width, length)) : [];
    let selectedDie;
    const isCarton = productId === PRODUCT_TYPE_CARTON;
    if (isCarton) {
        const diesMatchingCartonSize = cartonDies.filter(
            die =>
                die.width === parseFloat(width) &&
                die.length === parseFloat(length) &&
                die.depth === parseFloat(depth) &&
                die.shape_id === parseInt(shapeCategory)
        );
        selectedDie = diesMatchingCartonSize[0];
    } else {
        // If we have matches, choose the first match as the "selected die".
        selectedDie = diesMatchingShapeAndSize[0];
    }

    const cartonCustomDies = cartonDies.filter(({shape_id}) => shape_id === SHAPE_CARTON_CUSTOM);

    return {
        diesMatchingShape,
        diesMatchingShapeAndSize,
        diesMatchingShapeOppositeCorners,
        cartonDies,
        cartonCustomDies,
        selectedDie,
        isCustomShape: shapeCategory === QUOTER_SHAPE_CUSTOM,
        isNewDie: isNewDie({
            shapeCategory,
            customShapeSubCategory,
            width,
            length,
            depth,
            selectedDie,
            isCarton
        })
    };
}

function mapOrderToState(prev, order, dies) {
    const {width, length, depth, is_square_corners} = order;

    const selectedDie = dies.find(die => die.id == order.size_id);
    const {shapeCategory, customShapeSubCategory} = shapeCategoryFromOrder(prev, order);

    const isCarton =
        order.product_type_id === PRODUCT_TYPE_GRAPHIC_DESIGN
            ? order.graphic_design_order_details.design_product_type_id === PRODUCT_TYPE_CARTON
            : order.product_type_id === PRODUCT_TYPE_CARTON;

    return {
        ...prev,
        width: parseFloat(width),
        length: parseFloat(length),
        depth: parseFloat(depth),
        corners: is_square_corners ? QUOTER_SQUARE_CORNER : QUOTER_ROUNDED_CORNER,
        selectedDie,
        shapeCategory,
        customShapeSubCategory,
        isCustomShape: shapeCategory === QUOTER_SHAPE_CUSTOM,
        isNewDie: isNewDie({
            shapeCategory,
            customShapeSubCategory,
            width,
            length,
            depth,
            selectedDie,
            isCarton
        })
    };
}

function isNewDie({
    shapeCategory,
    customShapeSubCategory,
    width,
    length,
    depth,
    selectedDie,
    isCarton
}) {
    // If the user selected "Create New Shape", this is a new die.
    if (customShapeSubCategory === QUOTER_SHAPE_NEW_SHAPE) return true;

    // If this is another custom shape sub category, or if the user is selecting a
    // previously used die, this is not a new die.
    if (shapeCategory === QUOTER_SHAPE_CUSTOM || shapeCategory === QUOTER_SHAPE_PREVIOUSLY_USED)
        return false;

    // Finally, if no die id is selected but width and height are provided, that means this
    // is a new die that doesn't exist in our inventory.
    if (selectedDie == null && parseFloat(width) > 0 && parseFloat(length) > 0 && !isCarton)
        return true;
    if (
        selectedDie == null &&
        parseFloat(width) > 0 &&
        parseFloat(length) > 0 &&
        parseFloat(depth) > 0 &&
        isCarton
    )
        return true;

    return false;
}

function mapStateToOrder(state) {
    const {width, length, depth, shapeCategory, corners, selectedDie, is_design_in_progress} =
        state;
    const shapeId = orderShapeIdFromQuoterShape(shapeCategory, width, length, depth);
    return {
        width,
        length,
        depth,
        is_design_in_progress,
        size_id: selectedDie?.id,
        is_square_corners: corners === QUOTER_SQUARE_CORNER,
        size_type_id: orderSizeTypeIdFromQuoterState(state),
        shape_id: shapeId
    };
}

function shapeCategoryFromOrder(state, order) {
    const {shape_id, size_id} = order;

    // If previously used is already selected, keep it there.
    if (state.shapeCategory === QUOTER_SHAPE_PREVIOUSLY_USED) {
        return {shapeCategory: state.shapeCategory};
    }

    switch (shape_id) {
        case SHAPE_SQUARE:
        case SHAPE_RECTANGLE:
            return {shapeCategory: QUOTER_SHAPE_SQUARE_RECTANGLE};
        case SHAPE_CIRCLE:
        case SHAPE_OVAL:
            return {shapeCategory: QUOTER_SHAPE_CIRCLE_OVAL};
        case SHAPE_CUSTOM:
            if (
                state.customShapeSubCategory === QUOTER_SHAPE_CHAPSTICK ||
                !!dieShapeFilter(QUOTER_SHAPE_CHAPSTICK)({id: size_id})
            ) {
                return {
                    shapeCategory: QUOTER_SHAPE_CUSTOM,
                    customShapeSubCategory: QUOTER_SHAPE_CHAPSTICK
                };
            } else if (
                state.customShapeSubCategory === QUOTER_SHAPE_POLYGON ||
                !!dieShapeFilter(QUOTER_SHAPE_POLYGON)({id: size_id})
            ) {
                return {
                    shapeCategory: QUOTER_SHAPE_CUSTOM,
                    customShapeSubCategory: QUOTER_SHAPE_POLYGON
                };
            } else if (
                state.customShapeSubCategory === QUOTER_SHAPE_TAMPER_SEAL ||
                !!dieShapeFilter(QUOTER_SHAPE_TAMPER_SEAL)({id: size_id})
            ) {
                return {
                    shapeCategory: QUOTER_SHAPE_CUSTOM,
                    customShapeSubCategory: QUOTER_SHAPE_TAMPER_SEAL
                };
            } else if (state.isNewDie) {
                return {
                    shapeCategory: QUOTER_SHAPE_CUSTOM,
                    customShapeSubCategory: QUOTER_SHAPE_NEW_SHAPE
                };
            } else {
                return {
                    shapeCategory: QUOTER_SHAPE_CUSTOM,
                    customShapeSubCategory: null
                };
            }
        case SHAPE_CARTON_AUTOLOCK:
        case SHAPE_CARTON_STRAIGHT_TUCK:
        case SHAPE_CARTON_CUSTOM:
            return {shapeCategory: shape_id};
        default:
            return {shapeCategory: state.shapeCategory};
    }
}

function orderShapeIdFromQuoterShape(shapeCategory, width, length, depth = 0) {
    const equalDimensions = width == length;
    switch (shapeCategory) {
        case QUOTER_SHAPE_SQUARE_RECTANGLE:
            return equalDimensions ? SHAPE_SQUARE : SHAPE_RECTANGLE;
        case QUOTER_SHAPE_CIRCLE_OVAL:
            return equalDimensions ? SHAPE_CIRCLE : SHAPE_OVAL;
        case QUOTER_SHAPE_CUSTOM:
            return SHAPE_CUSTOM;
        case SHAPE_CARTON_AUTOLOCK:
        case SHAPE_CARTON_STRAIGHT_TUCK:
            return shapeCategory;
        default:
            return null;
    }
}

function orderSizeTypeIdFromQuoterState(state) {
    const {shapeCategory, customShapeSubCategory, selectedDie} = state;

    // If we've already selected a die, we want to report its size_type_id.
    if (selectedDie) return selectedDie.size_type_id;

    // If a die isn't selected, use the appropriate size_type_id for the selected shape category.
    switch (shapeCategory) {
        case QUOTER_SHAPE_SQUARE_RECTANGLE:
        case QUOTER_SHAPE_CIRCLE_OVAL:
            return SIZE_TYPE_STANDARD_NEW;
        case QUOTER_SHAPE_CUSTOM:
            if (customShapeSubCategory === QUOTER_SHAPE_NEW_SHAPE) {
                return SIZE_TYPE_CUSTOM_NEW;
            } else {
                return null;
            }
        default:
            return null;
    }
}

/**
 * Returns a filter function that returns true for dies matching shape and corners.
 *
 * @param {*} shapeCategory the selected shape value from the quoter's state
 * @param {*} corners the selected corners value from the quoter's state
 */
export const dieShapeFilter = (shapeCategory, corners) => die => {
    if (die == null) return false;

    // To keep this performant, please keep the conditionals below constant time, i.e.,
    // don't loop.
    switch (shapeCategory) {
        case QUOTER_SHAPE_SQUARE_RECTANGLE:
            return (
                (die.shape_id === SHAPE_SQUARE || die.shape_id === SHAPE_RECTANGLE) &&
                (corners === QUOTER_SQUARE_CORNER ? die.is_square_corners : !die.is_square_corners)
            );
        case QUOTER_SHAPE_CIRCLE_OVAL:
            return die.shape_id === SHAPE_CIRCLE || die.shape_id === SHAPE_OVAL;
        case QUOTER_SHAPE_CHAPSTICK:
            return QUOTER_CUSTOM_SIZES[die.id] === QUOTER_SHAPE_CHAPSTICK;
        case QUOTER_SHAPE_POLYGON:
            return QUOTER_CUSTOM_SIZES[die.id] === QUOTER_SHAPE_POLYGON;
        case QUOTER_SHAPE_TAMPER_SEAL:
            return QUOTER_CUSTOM_SIZES[die.id] === QUOTER_SHAPE_TAMPER_SEAL;
    }

    return false;
};

/**
 *
 * Returns a filter function that returns true for dies matching the provided width and
 * length.
 *
 * @param {*} width the width value from the quoter's state
 * @param {*} length the length value from the quoter's state
 */
export const dieSizeFilter = (width, length) => die => die.width == width && die.length == length;
